import { useEffect, useRef, useState } from "react";
import { Box, Modal } from "@mui/material";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";

import { useAuthDetails } from "../../../Common/cookiesHelper";
import Config from "../../../Constants/Config";
import { ciUrl } from "../../../Constants/nodeCi";
import { getSessionAuthHeader, getSessionFields } from "../../../Common/sessionAuth";
import { PERMISSIONS } from "../../../Constants/permission";
import { fetchGroupAPI } from "../../../Redux/features/SettingSetup/SettingSetupSlice";
import { fetchStoreList } from "../../../Redux/features/StoreSettingOption/StoreSettingOptionSlice";
import AccessDeniedTooltip from "../../../reuseableComponents/AccessDeniedTooltip";
import Icon from "../../NewInventory/Icon";
import useStickyBelowLayoutHeader from "../../../hooks/useStickyBelowLayoutHeader";
import "../../../Styles/CustomerManagement/customerWorkspace.css";

const GROUP_JOB_STATUS_POLL_MS = 3000;

const StoreGroups = () => {
  const { LoginGetDashBoardRecordJson, userTypeData, GetSessionLogin } =
    useAuthDetails();
  const dispatch = useDispatch();
  const { storeLoadingStatus } = useSelector(
    (state) => state.settingStoreOption
  );
  const merchant_id = LoginGetDashBoardRecordJson?.data?.merchant_id;
  const login_user_email = LoginGetDashBoardRecordJson?.data?.email;
  const login_employee_id =
    LoginGetDashBoardRecordJson?.employee_id ||
    LoginGetDashBoardRecordJson?.data?.employee_id ||
    "";
  const login_type = userTypeData?.login_type || "";
  const auth_token = userTypeData?.token || "";
  const auth_token_id = userTypeData?.token_id || "";
  const auth_session_id = userTypeData?.session_id || "";
  const [resolvedStoreLookupEmail, setResolvedStoreLookupEmail] = useState("");
  const [isLookupEmailReady, setIsLookupEmailReady] = useState(false);
  const resolvedEmailKeyRef = useRef("");
  const employeeLookupCancelRef = useRef(null);
  const storeLookupCancelRef = useRef(null);
  const groupFetchCancelRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const handleClose = () => {
    setOpen(false);
    setDropdownOptions([]);
  };

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningJobCount, setRunningJobCount] = useState(0);
  const jobPollTimerRef = useRef(null);

  useEffect(() => {
    if (login_type !== "manager") {
      setIsLookupEmailReady(true);
    }
  }, [login_type]);

  useEffect(() => {
    let cancelled = false;

    const checkRunningJobs = async () => {
      try {
        const response = await axios.get(
          Config.BASE_URL + Config.GROUP_JOB_STATUS_API,
          { headers: getSessionAuthHeader(), params: getSessionFields() }
        );
        if (cancelled) return;
        const count = Number(response?.data?.data?.count) || 0;
        setRunningJobCount(count);
        if (count > 0) {
          jobPollTimerRef.current = setTimeout(
            checkRunningJobs,
            GROUP_JOB_STATUS_POLL_MS
          );
        }
      } catch (error) {
        if (cancelled) return;
        setRunningJobCount(0);
      }
    };

    checkRunningJobs();
    return () => {
      cancelled = true;
      clearTimeout(jobPollTimerRef.current);
    };
  }, []);

  const isGroupJobRunning = runningJobCount > 0;

  const fetchGroupData = async () => {
    setLoading(true);
    if (groupFetchCancelRef.current) {
      groupFetchCancelRef.current.cancel("Cancelled previous group fetch");
    }
    const source = axios.CancelToken.source();
    groupFetchCancelRef.current = source;
    const formData = new FormData();
    formData.append("merchant_id", merchant_id);
    formData.append("email", login_user_email);

    await dispatch(fetchGroupAPI({ formData, cancelToken: source.token }))
      .unwrap()
      .then((res) => {
        if (res?.data?.length > 0) {
          setGroups(res.data);
        }
      })
      .catch((error) => {
        if (error?.cancelled) return;
        console.log("Error", error);
      })
      .finally(() => {
        if (groupFetchCancelRef.current === source) {
          setLoading(false);
        }
      });
  };

  const fetchStoreData = async (inputValue, merchantList) => {
    if (login_type === "manager" && !isLookupEmailReady) {
      return [];
    }
    if (storeLookupCancelRef.current) {
      storeLookupCancelRef.current.cancel("Cancelled previous store lookup");
    }
    const source = axios.CancelToken.source();
    storeLookupCancelRef.current = source;
    const payload = {
      name: inputValue,
      merchant_id: merchant_id,
      ...userTypeData,
      email:
        userTypeData?.login_type === "superadmin"
          ? LoginGetDashBoardRecordJson?.data?.email
          : GetSessionLogin?.username,
      cancelToken: source.token,
    };

    try {
      const response = await dispatch(fetchStoreList(payload)).unwrap().then();
      if (response?.status && response.data?.length > 0) {
        let splitMerchantList = merchantList?.split(",");
        const filterItems = response?.data?.filter((store) => {
          return splitMerchantList?.includes(store?.merchant_id?.trim());
        });

        const normalizedTags = filterItems.map((store) => ({
          label: store.name,
          value: store.merchant_id,
        }));
        setDropdownOptions(normalizedTags);
      }
      return [];
    } catch (error) {
      if (error?.cancelled) {
        return [];
      }
      console.error("Error fetching stores:", error);
      return [];
    }
  };

  useEffect(() => {
    if (login_type === "manager" && !isLookupEmailReady) {
      return;
    }
    if (!merchant_id || !login_user_email) {
      return;
    }
    fetchGroupData();
    return () => {
      if (groupFetchCancelRef.current) {
        groupFetchCancelRef.current.cancel("Cleanup group fetch");
      }
    };
  }, [
    login_type,
    isLookupEmailReady,
    resolvedStoreLookupEmail,
    merchant_id,
    login_user_email,
  ]);

  useEffect(() => {
    const resolveLookupEmail = async () => {
      if (login_type !== "manager") {
        setResolvedStoreLookupEmail(login_user_email || "");
        setIsLookupEmailReady(true);
        return;
      }

      if (!login_employee_id || !merchant_id) {
        setResolvedStoreLookupEmail(login_user_email || "");
        setIsLookupEmailReady(true);
        return;
      }

      const resolveKey = `${login_type}|${merchant_id}|${login_employee_id}|${auth_token}`;
      if (resolvedEmailKeyRef.current === resolveKey) {
        return;
      }
      setIsLookupEmailReady(false);
      try {
        if (employeeLookupCancelRef.current) {
          employeeLookupCancelRef.current.cancel(
            "Cancelled previous employee lookup"
          );
        }
        const source = axios.CancelToken.source();
        employeeLookupCancelRef.current = source;
        const payload = {
          merchant_id: merchant_id?.toUpperCase?.() || merchant_id,
          employee_id: login_employee_id,
          login_type,
          token: auth_token,
          token_id: auth_token_id,
          session_id: auth_session_id,
        };
        const response = await axios.post(
          ciUrl(Config.EMPLOYEE_DATA),
          payload,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${auth_token}`,
            },
            cancelToken: source.token,
          }
        );
        const employeeData =
          response?.data?.data ||
          response?.data?.result ||
          response?.data?.employeeData ||
          {};
        const employeeEmail =
          employeeData?.email ||
          employeeData?.email_id ||
          employeeData?.employee_email ||
          "";
        const finalEmail = employeeEmail || login_user_email || "";
        setResolvedStoreLookupEmail(finalEmail);
        resolvedEmailKeyRef.current = resolveKey;
        setIsLookupEmailReady(true);
      } catch (error) {
        if (axios.isCancel(error)) return;
        setResolvedStoreLookupEmail(login_user_email || "");
        setIsLookupEmailReady(true);
      }
    };
    resolveLookupEmail();
    return () => {
      if (employeeLookupCancelRef.current) {
        employeeLookupCancelRef.current.cancel("Cleanup employee lookup");
      }
    };
  }, [
    login_type,
    merchant_id,
    login_employee_id,
    login_user_email,
    auth_token,
    auth_token_id,
    auth_session_id,
  ]);

  const handleOpenTheMerchantModal = (merchantList) => {
    if (login_type === "manager" && !isLookupEmailReady) {
      return;
    }
    setOpen(true);
    fetchStoreData("", merchantList);
  };

  const { permissions } = useSelector((state) => state.permissions);
  const hasGroupManagementAccessPermission =
    !permissions.length ||
    permissions.indexOf(PERMISSIONS.MENU_ACCESS_GROUP_MANAGEMENT) > -1;
  const hasAddStoreGroupPermission =
    hasGroupManagementAccessPermission &&
    (!permissions.length ||
      permissions.indexOf(PERMISSIONS.GROUP_MANAGEMENT_CREATE) > -1);
  const hasEditStoreGroupPermission =
    hasGroupManagementAccessPermission &&
    (!permissions.length ||
      permissions.indexOf(PERMISSIONS.GROUP_MANAGEMENT_EDIT) > -1);

  const isTableLoading =
    loading || (login_type === "manager" && !isLookupEmailReady);
  const stickyTop = useStickyBelowLayoutHeader();

  return (
    <div className="qv-groups-page">
      <div className="qv-customer-header">
        <div className="qv-customer-header__title">
          <h1>Customer Groups</h1>
          <p>Create and manage groups of store locations</p>
        </div>
        <div className="qv-customer-header__actions">
          <AccessDeniedTooltip hasPermission={hasAddStoreGroupPermission}>
            <Link
              className="qv-customer-btn-primary"
              to="/customer-management/create-group"
              style={
                isGroupJobRunning
                  ? { opacity: 0.55, pointerEvents: "none" }
                  : undefined
              }
              title={
                isGroupJobRunning
                  ? "Group sync in progress — please wait until it finishes"
                  : undefined
              }
              onClick={(e) => {
                if (!hasAddStoreGroupPermission || isGroupJobRunning) {
                  e.preventDefault();
                }
              }}
            >
              <Icon name="plus" size={14} color="var(--qv-brand-on, #fff)" />
              Create Customer Group
            </Link>
          </AccessDeniedTooltip>
        </div>
      </div>

      {isGroupJobRunning ? (
        <p className="qv-groups-note">
          Group sync is in progress. Creating and editing is paused until it finishes.
        </p>
      ) : null}

      <div
        className="qv-groups-table-card"
        style={{ "--qv-groups-sticky-top": `${stickyTop}px` }}
      >
        <table className="qv-groups-table">
          <thead>
            <tr>
              <th>Group ID</th>
              <th>Group Name</th>
              <th>Merchants</th>
              <th className="qv-groups-table__actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isTableLoading ? (
              [1, 2, 3, 4, 5].map((row) => (
                <tr key={row}>
                  <td>
                    <Skeleton />
                  </td>
                  <td>
                    <Skeleton />
                  </td>
                  <td>
                    <Skeleton />
                  </td>
                  <td className="qv-groups-table__actions">
                    <Skeleton width={28} height={28} />
                  </td>
                </tr>
              ))
            ) : groups.length > 0 ? (
              groups.map((row) => (
                <tr key={row?.id}>
                  <td>{row?.id}</td>
                  <td>
                    <div className="qv-groups-name" title={row?.name}>
                      {row?.name?.length < 50
                        ? row?.name
                        : row?.name?.slice(0, 50) + `...` || ""}
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="qv-groups-link"
                      onClick={() => handleOpenTheMerchantModal(row?.merchants)}
                    >
                      View Merchants
                    </button>
                  </td>
                  <td className="qv-groups-table__actions">
                    <div className="qv-groups-actions">
                      <AccessDeniedTooltip
                        hasPermission={hasEditStoreGroupPermission}
                      >
                        <Link
                          to={`/customer-management/edit-group?id=${row?.id}`}
                          state={{ id: row?.id }}
                          className="qv-customer-icon-btn"
                          style={
                            isGroupJobRunning
                              ? { opacity: 0.55, pointerEvents: "none" }
                              : undefined
                          }
                          title={
                            isGroupJobRunning
                              ? "Group sync in progress — please wait until it finishes"
                              : "Edit group"
                          }
                          onClick={(e) => {
                            if (
                              !hasEditStoreGroupPermission ||
                              isGroupJobRunning
                            ) {
                              e.preventDefault();
                            }
                          }}
                        >
                          <Icon name="edit" size={15} />
                        </Link>
                      </AccessDeniedTooltip>
                      {!row.merchants ? (
                        <span className="qv-customer-icon-btn" aria-hidden>
                          <Icon name="trash" size={15} />
                        </span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="qv-groups-empty">
                  No customer groups yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="qv-groups-merchants-title"
      >
        <Box className="qv-groups-modal">
          <div className="qv-groups-modal__header">
            <h2 id="qv-groups-merchants-title" className="qv-groups-modal__title">
              Merchants
            </h2>
            <button
              type="button"
              className="qv-customer-icon-btn"
              onClick={handleClose}
              aria-label="Close"
            >
              <Icon name="close" size={16} />
            </button>
          </div>
          <div className="qv-groups-modal__body">
            {storeLoadingStatus ? (
              <div>
                <Skeleton height={18} width="60%" />
                <Skeleton height={18} width="45%" />
              </div>
            ) : dropdownOptions && dropdownOptions.length >= 1 ? (
              dropdownOptions.map((item) => (
                <div className="qv-groups-merchant" key={item.value}>
                  {item.label}
                </div>
              ))
            ) : (
              <p className="qv-groups-status">No merchants found</p>
            )}
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default StoreGroups;
